import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import { theme } from "@clearabee/ui-library-native";
import { useCatalogue, hyphenatedToNormal } from "@clearabee/ui-sdk";
import { useRouter } from "lib/hooks/useRouter";
import { ErrorPage } from "components/ErrorPage/ErrorPage";
import { Layout } from "components/Layout/Layout";
import { useForm, Form } from "lib/hooks/useForm";
import {
  FormState,
  validationSchema,
  stateToQuery,
  queryToState,
  QueryParams,
} from "./SkipBags.validation";
import { Question } from "components/Question/Question";
import { QuestionContextProvider } from "lib/providers/questionProvider";
import { SelectBagCollectioOnlyBags } from "./CollectionOnly/SelectBagCollectionOnlyBags";
import { RefAndPostcode } from "./CollectionOnly/RefAndPostcode";
import { IsPaid } from "./CollectionOnly/IsPaid";
import { SelectType } from "./SelectType";
import { SelectBagBagOnly } from "./SelectBagBagOnly";
import { SelectBagBagAndCollection } from "./SelectBagBagAndCollection";
import { SKIPBAGS_TYPES } from "lib/constants/skipBagTypes";
import { getCollectionBagTitle } from "./helpers";
import { BagContents } from "./CollectionOnly/BagContents";
import { Location } from "./CollectionOnly/Location";
import isEqual from "fast-deep-equal";
import { LoadQueryParams } from "./LoadQueryParams";

interface SkipBagsProps {
  queryParams?: QueryParams;
}

const SkipBagQuestion = Question<FormState>();

export const SkipBags = ({
  queryParams: initialQueryParams = {},
}: SkipBagsProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { navigate, replace } = useRouter();
  const { defaultCatalogue } = useCatalogue();
  const service = defaultCatalogue.items.find(({ sku }) => sku === "SKIPBAGS");
  const queryParams = queryToState(initialQueryParams);

  useEffect(() => {
    if (!queryParams) {
      navigate({
        routeName: "ServicePage",
        params: { categorySku: "SKIPBAGS" },
      });
    }
  }, []);

  const form = useForm({
    validationSchema,
    defaultValues: queryParams ?? {},
    reValidateMode: "onChange",
    onSubmit: (values) => {
      const { products, bagContents, distanceFromRoad } = values;

      const isHandballCharge =
        bagContents?.types.includes("tiles") &&
        distanceFromRoad === "Between 4 and 10 metres";

      const selectedItems = [
        ...Object.entries(products || {}).filter(([, qty]) => !!qty),
        ...(isHandballCharge ? [["HANDBALLCHARGE", 1]] : []),
      ];

      navigate({
        routeName: "BulkAdd",
        params: {
          categorySku: "SKIPBAGS",
          ...stateToQuery(values),
          products: selectedItems.map((entry) => entry.join(",")).join(","),
          postcode: values.refAndPostcode?.postcode,
        },
      });
    },
  });

  const {
    formState: { errors, isValid },
    getValues,
  } = form;
  const valuesRef = useRef(getValues());

  useEffect(() => {
    if (!!errors && isValid) {
      form.clearErrors();
    }
  }, [isValid]);

  useEffect(() => {
    const values = getValues();

    if (isEqual(values, valuesRef.current)) {
      return;
    }

    valuesRef.current = values;

    const fn = Platform.OS === "web" ? replace : navigate;
    const params = { ...stateToQuery(values), categorySku: "SKIPBAGS" };

    fn({ routeName: "ServicePage", params, shallow: true });
  });

  if (!service) return <ErrorPage />;

  return (
    <Layout
      title={translate("hwa.screens.selectProduct.title")}
      backTo={{ routeName: "Book" }}
      contentWidth="xlarge"
    >
      <View
        style={{ maxWidth: theme.screens.large, margin: "auto", width: "100%" }}
      >
        <Form state={form}>
          <QuestionContextProvider initVisibleState={{ type: "open" }}>
            <LoadQueryParams queryParams={queryParams} />
            <SkipBagQuestion
              question={translate("hwa.components.skipBags.questions.type")}
              id="type"
              formatAnswer={(value) => SKIPBAGS_TYPES[value]}
            >
              <SelectType />
            </SkipBagQuestion>
            <SkipBagQuestion
              previousId="type"
              question={translate("hwa.components.skipBags.questions.isPaid")}
              id="isPaid"
            >
              <IsPaid />
            </SkipBagQuestion>
            <SkipBagQuestion
              question={translate(
                "hwa.components.skipBags.questions.refAndPostcode",
              )}
              previousId="type"
              id="refAndPostcode"
              formatAnswer={({ postcode, ref }) => {
                return `${ref.toUpperCase()} - ${postcode.toUpperCase()}`;
              }}
            >
              <RefAndPostcode />
            </SkipBagQuestion>
            <SkipBagQuestion
              question={translate(
                "hwa.components.skipBags.questions.selectBags",
              )}
              previousId="type"
              id="products"
              formatAnswer={(values) =>
                Object.entries(values)
                  .filter((item) => item[1])
                  .map((item) => `${item[1]} ${getCollectionBagTitle(item[0])}`)
                  .join(", ")
              }
            >
              {({ previousValue }) => (
                <>
                  {previousValue === "BAGCOLLECTIONSKIPBAGS" && (
                    <SelectBagBagAndCollection />
                  )}

                  {previousValue === "BAGONLYSKIPBAGS" && <SelectBagBagOnly />}
                  {previousValue === "COLLECTIONONLYSKIPBAGS" && (
                    <SelectBagCollectioOnlyBags />
                  )}
                </>
              )}
            </SkipBagQuestion>
            <SkipBagQuestion
              question={translate(
                "hwa.components.skipBags.questions.bagContents",
              )}
              previousId="products"
              id="bagContents"
              formatAnswer={(answer) =>
                answer.types.map(hyphenatedToNormal).join(", ")
              }
            >
              <BagContents />
            </SkipBagQuestion>
            <SkipBagQuestion
              question={translate(
                "hwa.components.skipBags.questions.distanceFromRoad",
              )}
              previousId="bagContents"
              id="distanceFromRoad"
            >
              <Location />
            </SkipBagQuestion>
          </QuestionContextProvider>
        </Form>
      </View>
    </Layout>
  );
};
